/**
 * Token Refresh Utilities
 * Handles automatic and proactive token refresh
 */

import { authApi } from "../api/auth";
import { tokenStorage } from "./tokenStorage";

interface DecodedToken {
  exp: number;
  userId: string;
  email: string;
  [key: string]: any;
}

/**
 * Decode JWT token to extract payload
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Failed to decode token:", error);
    return null;
  }
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const now = Date.now();
  const expiresAt = decoded.exp * 1000; // Convert to milliseconds

  return now >= expiresAt;
}

/**
 * Check if token should be refreshed
 * Returns true if token expires within the threshold (default: 30 minutes)
 */
export function shouldRefreshToken(
  token: string,
  thresholdMinutes: number = 30
): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const now = Date.now();
  const expiresAt = decoded.exp * 1000; // Convert to milliseconds
  const timeUntilExpiry = expiresAt - now;
  const thresholdMs = thresholdMinutes * 60 * 1000;

  // Refresh if expired or within threshold
  return timeUntilExpiry <= thresholdMs;
}

/**
 * Get time until token expires (in milliseconds)
 */
export function getTimeUntilExpiry(token: string): number {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return 0;

  const now = Date.now();
  const expiresAt = decoded.exp * 1000;
  const timeUntilExpiry = expiresAt - now;

  return Math.max(0, timeUntilExpiry);
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(): Promise<{
  token: string;
  refreshToken: string;
} | null> {
  const refreshToken = tokenStorage.getRefreshToken();

  if (!refreshToken) {
    console.warn("No refresh token available");
    return null;
  }

  try {
    const response = await authApi.refreshToken(refreshToken);

    // Update secure storage
    tokenStorage.setAccessToken(response.token);
    if (response.refreshToken) {
      tokenStorage.setRefreshToken(response.refreshToken);
    }
    if (response.user) {
      tokenStorage.setUser(response.user);
    }

    console.log("✅ Token refreshed successfully");

    return {
      token: response.token,
      refreshToken: response.refreshToken || refreshToken,
    };
  } catch (error) {
    console.error("Failed to refresh token:", error);

    // Clear tokens on refresh failure
    tokenStorage.clearAll();

    return null;
  }
}

/**
 * Token Refresh Manager
 * Manages activity-based token refresh checks
 * Only refreshes tokens when user is active to prevent indefinite sessions
 */
export class TokenRefreshManager {
  private intervalId: number | null = null;
  private checkIntervalMs: number = 5 * 60 * 1000; // 5 minutes
  private refreshThresholdMinutes: number = 5; // Refresh when < 5 min remaining (safety buffer)
  private isRefreshing: boolean = false;
  private onTokenRefreshed?: (token: string) => void;
  private onRefreshFailed?: () => void;
  private lastActivityTime: number = Date.now();
  private activityTimeoutMinutes: number = 30; // Consider inactive after 30 minutes
  private activityEvents: string[] = [
    "mousedown",
    "keydown",
    "scroll",
    "touchstart",
    "click",
  ];

  constructor(options?: {
    checkIntervalMs?: number;
    refreshThresholdMinutes?: number;
    activityTimeoutMinutes?: number;
    onTokenRefreshed?: (token: string) => void;
    onRefreshFailed?: () => void;
  }) {
    if (options?.checkIntervalMs) {
      this.checkIntervalMs = options.checkIntervalMs;
    }
    if (options?.refreshThresholdMinutes !== undefined) {
      this.refreshThresholdMinutes = options.refreshThresholdMinutes;
    }
    if (options?.activityTimeoutMinutes !== undefined) {
      this.activityTimeoutMinutes = options.activityTimeoutMinutes;
    }
    if (options?.onTokenRefreshed) {
      this.onTokenRefreshed = options.onTokenRefreshed;
    }
    if (options?.onRefreshFailed) {
      this.onRefreshFailed = options.onRefreshFailed;
    }
  }

  /**
   * Start the token refresh manager
   */
  start(): void {
    if (this.intervalId) {
      console.warn("Token refresh manager already started");
      return;
    }

    console.log("🔄 Starting token refresh manager (activity-based)");

    // Set up activity listeners
    this.setupActivityListeners();

    // Check immediately on start
    this.checkAndRefresh();

    // Then check periodically
    this.intervalId = window.setInterval(() => {
      this.checkAndRefresh();
    }, this.checkIntervalMs);
  }

  /**
   * Stop the token refresh manager
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      this.removeActivityListeners();
      console.log("🛑 Token refresh manager stopped");
    }
  }

  /**
   * Set up activity listeners to track user activity
   */
  private setupActivityListeners(): void {
    this.activityEvents.forEach((event) => {
      window.addEventListener(event, this.handleActivity, { passive: true });
    });
  }

  /**
   * Remove activity listeners
   */
  private removeActivityListeners(): void {
    this.activityEvents.forEach((event) => {
      window.removeEventListener(event, this.handleActivity);
    });
  }

  /**
   * Handle user activity event
   */
  private handleActivity = (): void => {
    this.lastActivityTime = Date.now();
  };

  /**
   * Check if user has been inactive for too long
   */
  private isUserInactive(): boolean {
    const now = Date.now();
    const timeSinceActivity = now - this.lastActivityTime;
    const inactiveThresholdMs = this.activityTimeoutMinutes * 60 * 1000;
    return timeSinceActivity > inactiveThresholdMs;
  }

  /**
   * Check if token needs refresh and refresh if needed
   * Only refreshes if user has been active recently
   */
  private async checkAndRefresh(): Promise<void> {
    // Prevent concurrent refresh attempts
    if (this.isRefreshing) {
      return;
    }

    const token = tokenStorage.getAccessToken();
    if (!token) {
      return;
    }

    // Check if user has been inactive
    if (this.isUserInactive()) {
      console.log(
        "⏸️ User inactive - skipping token refresh to allow natural expiry"
      );
      return;
    }

    // Check if token should be refreshed
    if (!shouldRefreshToken(token, this.refreshThresholdMinutes)) {
      const timeUntilExpiry = getTimeUntilExpiry(token);
      const minutesRemaining = Math.floor(timeUntilExpiry / 60000);
      console.log(
        `⏱️ Token is still valid (${minutesRemaining} minutes remaining)`
      );
      return;
    }

    console.log("🔄 Token needs refresh, initiating refresh...");
    this.isRefreshing = true;

    try {
      const result = await refreshAccessToken();

      if (result) {
        console.log("✅ Token refreshed via manager");
        if (this.onTokenRefreshed) {
          this.onTokenRefreshed(result.token);
        }
      } else {
        console.error("❌ Token refresh failed");
        if (this.onRefreshFailed) {
          this.onRefreshFailed();
        }
      }
    } catch (error) {
      console.error("❌ Token refresh error:", error);
      if (this.onRefreshFailed) {
        this.onRefreshFailed();
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Force an immediate token refresh check
   */
  async forceRefresh(): Promise<void> {
    await this.checkAndRefresh();
  }
}
